import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { axios } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { savePendingVoter, isOnline } from '../utils/offlineDB';
import { COMMUNITIES, PROFESSIONS, DISTRICTS, EDUCATION_LIST } from '../constants/masterData';

const RELATION_TYPES = ['S/o','D/o','W/o','H/o','C/o'];
const HOUSE_RELATIONS = ['Son','Daughter','Mother','Father','Wife','Husband','Brother','Sister','Grandfather','Grandmother','Grandson','Granddaughter','Uncle','Aunt','Other'];
const MOTHER_TONGUES = ['Telugu','Hindi','Urdu','Tamil','Kannada','Marathi','Bengali','English','Other'];

const inputSt = (focused, field) => ({
  width:'100%', padding:'11px 14px',
  border:`2px solid ${focused===field?'#FF6B00':'rgba(255,107,0,0.2)'}`,
  borderRadius:12, fontSize:'0.88rem',
  fontFamily:"'Outfit',sans-serif",
  background:focused===field?'white':'rgba(255,255,255,0.85)',
  outline:'none', transition:'all 0.2s',
  boxShadow:focused===field?'0 0 0 4px rgba(255,107,0,0.1)':'none',
  WebkitAppearance:'none',
});

const Label = ({children, required}) => (
  <label style={{display:'block',fontSize:'0.72rem',fontWeight:700,color:'#BF360C',marginBottom:5,textTransform:'uppercase',letterSpacing:'0.7px'}}>
    {children}{required&&<span style={{color:'#E53935',marginLeft:2}}>*</span>}
  </label>
);

const Section = ({icon, title}) => (
  <div style={{fontFamily:"'Baloo 2',sans-serif",fontSize:'0.95rem',fontWeight:700,color:'#BF360C',marginBottom:16,marginTop:8,display:'flex',alignItems:'center',gap:8,paddingBottom:8,borderBottom:'2px solid rgba(255,107,0,0.1)'}}>
    <span>{icon}</span>{title}
  </div>
);

export default function EnrollVoter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [gettingLoc, setGettingLoc] = useState(false);
  const [focused, setFocused] = useState('');
  const [houseData, setHouseData] = useState(null);
  const [houseCheckLoading, setHouseCheckLoading] = useState(false);
  const [serialLoading, setSerialLoading] = useState(false);
  const houseTimer = useRef(null);
  const boothTimer = useRef(null);

  const [form, setForm] = useState({
    name:'', relation_name:'', relation_type:'S/o',
    age:'', gender:'', phone:'',
    profession:'', community:'', degree:'',
    mother_tongue:'', reference_name:'',
    district:'', address:'', booth_number:'',
    serial_number:'', epic_id:'', area:'', house_number:'', voter_id:'',
    latitude:'', longitude:'',
    relation_to_head:'', house_head_id:'',
    status:'None',
  });

  const f = (field, value) => setForm(p => ({...p, [field]:value}));
  const fo = field => () => setFocused(field);
  const bl = () => setFocused('');

  // Auto-fetch next serial when booth number entered
  useEffect(() => {
    if (boothTimer.current) clearTimeout(boothTimer.current);
    if (!form.booth_number) return;
    boothTimer.current = setTimeout(async () => {
      setSerialLoading(true);
      try {
        const { data } = await axios.get(`/voters/next-serial?booth_number=${form.booth_number}`);
        f('serial_number', String(data.next_serial));
        if (data.last_serial > 0) {
          toast(`📋 Auto serial: ${data.next_serial} (booth has ${data.last_serial} voters)`, {icon:'🔢', duration:3000});
        }
      } catch {}
      finally { setSerialLoading(false); }
    }, 700);
  }, [form.booth_number]);

  // House check for family mapping
  useEffect(() => {
    if (houseTimer.current) clearTimeout(houseTimer.current);
    if (!form.house_number || form.house_number.length < 2) { setHouseData(null); return; }
    houseTimer.current = setTimeout(async () => {
      setHouseCheckLoading(true);
      try {
        const params = { house_number: form.house_number };
        if (form.area) params.area = form.area;
        if (form.district) params.district = form.district;
        const { data } = await axios.get('/voters/house-check', { params });
        setHouseData(data);
        if (data.exists) toast(`🏠 ${data.members.length} member(s) found at this house!`, {icon:'👥', duration:3000});
      } catch {}
      finally { setHouseCheckLoading(false); }
    }, 700);
  }, [form.house_number, form.area, form.district]);

  const getLocation = () => {
    if (!navigator.geolocation) { toast.error('GPS not available'); return; }
    setGettingLoc(true);
    navigator.geolocation.getCurrentPosition(
      pos => { f('latitude', pos.coords.latitude); f('longitude', pos.coords.longitude); toast.success('📍 Location captured!'); setGettingLoc(false); },
      () => { toast.error('Location denied'); setGettingLoc(false); }
    );
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Voter name is required'); return; }
    if (!form.gender) { toast.error('Gender is required'); return; }
    if (form.phone && form.phone.length !== 10) { toast.error('Phone must be 10 digits'); return; }
    if (houseData?.exists && !form.relation_to_head) { toast.error('Please select relation to head of house'); return; }

    setLoading(true);
    try {
      const payload = {
        ...form,
        mobile: form.phone,
        relation_name: form.relation_name
          ? `${form.relation_name}`
          : houseData?.exists && houseData?.headPerson
            ? `${form.relation_to_head} of ${houseData.headPerson.name}`
            : '',
      };

      if (!isOnline()) {
        await savePendingVoter(payload);
        toast.success('✅ Saved offline! Will sync when internet returns.', {duration:5000, icon:'📴'});
        navigate('/');
        return;
      }

      const { data } = await axios.post('/voters', payload);
      toast.success('✅ Voter enrolled successfully!');
      navigate(`/voter/${data.id}`);
    } catch(err) { toast.error(err.response?.data?.error || 'Enrollment failed'); }
    finally { setLoading(false); }
  };

  const ageOptions = Array.from({length:83}, (_,i) => i+18);

  return (
    <div style={{minHeight:'100vh', background:'linear-gradient(160deg,#FFF4ED,#FFF8F0)', fontFamily:"'Outfit',sans-serif"}}>
      <div className="app-header">
        <div style={{display:'flex', alignItems:'center', gap:10}}>
          <button className="back-btn" onClick={()=>navigate('/')}>←</button>
          <div>
            <div style={{fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:'1.1rem', color:'white'}}>➕ Enroll New Voter</div>
            <div style={{fontSize:'0.68rem', opacity:.8, color:'white'}}>Fill all details carefully</div>
          </div>
        </div>
      </div>

      <div style={{maxWidth:900, margin:'0 auto', padding:'20px 16px 60px'}}>
        <div style={{background:'rgba(255,255,255,0.9)', backdropFilter:'blur(16px)', borderRadius:24, padding:'24px 28px', boxShadow:'0 8px 40px rgba(255,107,0,0.1)', border:'1px solid rgba(255,255,255,0.7)'}}>

          {/* ── SECTION 1: Personal ── */}
          <Section icon="👤" title="Personal Details"/>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'0 24px'}}>

            <div style={{marginBottom:14}}>
              <Label required>Full Name</Label>
              <input style={inputSt(focused,'name')} placeholder="Enter full name" value={form.name} onChange={e=>f('name',e.target.value)} onFocus={fo('name')} onBlur={bl}/>
            </div>

            {/* Relative Name + Type */}
            <div style={{marginBottom:14}}>
              <Label>Relative Name</Label>
              <div style={{display:'flex', gap:6}}>
                <select style={{...inputSt(focused,'rel_type'), width:'90px', flexShrink:0}} value={form.relation_type} onChange={e=>f('relation_type',e.target.value)} onFocus={fo('rel_type')} onBlur={bl}>
                  {RELATION_TYPES.map(r=><option key={r} value={r}>{r}</option>)}
                </select>
                <input style={inputSt(focused,'relation_name')} placeholder="Father/Husband name" value={form.relation_name} onChange={e=>f('relation_name',e.target.value)} onFocus={fo('relation_name')} onBlur={bl}/>
              </div>
            </div>

            <div style={{marginBottom:14}}>
              <Label required>Age</Label>
              <select style={inputSt(focused,'age')} value={form.age} onChange={e=>f('age',e.target.value)} onFocus={fo('age')} onBlur={bl}>
                <option value="">Select Age</option>
                {ageOptions.map(a=><option key={a} value={a}>{a}</option>)}
              </select>
            </div>

            <div style={{marginBottom:14}}>
              <Label required>Gender</Label>
              <select style={inputSt(focused,'gender')} value={form.gender} onChange={e=>f('gender',e.target.value)} onFocus={fo('gender')} onBlur={bl}>
                <option value="">Select Gender</option>
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="O">Other</option>
              </select>
            </div>

            <div style={{marginBottom:14}}>
              <Label>Phone (10 digits)</Label>
              <div style={{position:'relative'}}>
                <input type="tel" maxLength={10} style={{...inputSt(focused,'phone'), paddingRight:50}} placeholder="9XXXXXXXXX" value={form.phone} onChange={e=>f('phone',e.target.value.replace(/\D/g,'').slice(0,10))} onFocus={fo('phone')} onBlur={bl}/>
                {form.phone.length>0&&<div style={{position:'absolute',right:10,top:'50%',transform:'translateY(-50%)',fontSize:'0.7rem',fontWeight:700,color:form.phone.length===10?'#00C853':'#FFB300'}}>{form.phone.length}/10</div>}
              </div>
            </div>

            <div style={{marginBottom:14}}>
              <Label>Voter ID</Label>
              <input style={inputSt(focused,'voter_id')} placeholder="e.g. AP/12/34/567890" value={form.voter_id||''} onChange={e=>f('voter_id',e.target.value)} onFocus={fo('voter_id')} onBlur={bl}/>
            </div>

            <div style={{marginBottom:14}}>
              <Label>UU ID</Label>
              <input style={{...inputSt(focused,'epic_id'), background:'#F5F5F5', color:'#9E6040'}} placeholder="Auto-generated" value={form.epic_id||''} readOnly title="Auto-generated based on name and house number"/>
              <div style={{fontSize:'0.68rem',color:'#9E6040',marginTop:3}}>Auto-generated from name + house number</div>
            </div>
          </div>

          {/* ── SECTION 2: Location ── */}
          <Section icon="🏠" title="Location & Booth Details"/>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'0 24px'}}>

            <div style={{marginBottom:14}}>
              <Label>District</Label>
              <select style={inputSt(focused,'district')} value={form.district} onChange={e=>f('district',e.target.value)} onFocus={fo('district')} onBlur={bl}>
                <option value="">-- Select District --</option>
                {DISTRICTS.map(d=><option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            <div style={{marginBottom:14}}>
              <Label>Area / Locality</Label>
              <input style={inputSt(focused,'area')} placeholder="Area / locality" value={form.area} onChange={e=>f('area',e.target.value)} onFocus={fo('area')} onBlur={bl}/>
            </div>

            <div style={{marginBottom:14}}>
              <Label>
                Booth Number
              </Label>
              <input type="number" style={inputSt(focused,'booth_number')} placeholder="Booth #" value={form.booth_number} onChange={e=>f('booth_number',e.target.value)} onFocus={fo('booth_number')} onBlur={bl}/>
              {form.serial_number && form.booth_number && (
                <div style={{fontSize:'0.7rem',color:'#2E7D32',marginTop:3,fontWeight:600,display:'flex',alignItems:'center',gap:4}}>
                  {serialLoading ? '⏳ Fetching serial...' : `✅ Auto Serial: ${form.serial_number}`}
                </div>
              )}
            </div>

            <div style={{marginBottom:14}}>
              <Label>
                House Number
                {houseCheckLoading && <span style={{color:'#FF9800',marginLeft:4,fontWeight:400}}>🔍 checking...</span>}
              </Label>
              <input style={inputSt(focused,'house_number')} placeholder="e.g. 12-45" value={form.house_number} onChange={e=>f('house_number',e.target.value)} onFocus={fo('house_number')} onBlur={bl}/>
            </div>

            <div style={{marginBottom:14}}>
              <Label>Status</Label>
              <select style={inputSt(focused,'status')} value={form.status} onChange={e=>f('status',e.target.value)} onFocus={fo('status')} onBlur={bl}>
                <option value="None">None</option>
                <option value="Available">Available</option>
                <option value="Shifted">Shifted</option>
                <option value="Expired">Expired</option>
              </select>
            </div>
          </div>

          {/* Address full width */}
          <div style={{marginBottom:14}}>
            <Label>Full Address</Label>
            <textarea rows={2} style={{...inputSt(focused,'address'), resize:'none'}} placeholder="Full residential address" value={form.address} onChange={e=>f('address',e.target.value)} onFocus={fo('address')} onBlur={bl}/>
          </div>

          {/* ── FAMILY MAPPING ── */}
          {houseData?.exists && houseData?.headPerson && (
            <div style={{marginBottom:16, borderRadius:16, overflow:'hidden', border:'2px solid rgba(255,107,0,0.25)', animation:'fadeInUp 0.3s ease'}}>
              <div style={{background:'linear-gradient(135deg,#FF6B00,#FF9933)', padding:'12px 16px', display:'flex', alignItems:'center', gap:10}}>
                <div style={{width:36,height:36,borderRadius:'50%',background:'rgba(255,255,255,0.2)',border:'2px solid rgba(255,255,255,0.4)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:'1rem',color:'white'}}>
                  {houseData.headPerson.name.charAt(0)}
                </div>
                <div style={{color:'white'}}>
                  <div style={{fontWeight:800,fontSize:'0.9rem'}}>🏠 House {form.house_number} — {houseData.members.length} member(s)</div>
                  <div style={{fontSize:'0.75rem',opacity:.9}}>Head: <strong>{houseData.headPerson.name}</strong> ({houseData.headPerson.gender==='M'?'Male':'Female'}, {houseData.headPerson.age} yrs)</div>
                </div>
              </div>
              {houseData.members.length > 1 && (
                <div style={{background:'rgba(255,107,0,0.04)', padding:'8px 16px', borderBottom:'1px solid rgba(255,107,0,0.1)'}}>
                  <div style={{fontSize:'0.7rem',fontWeight:700,color:'#9E6040',marginBottom:5,textTransform:'uppercase'}}>People in this house:</div>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {houseData.members.map(m=>(
                      <span key={m.id} style={{background:'white',border:'1px solid rgba(255,107,0,0.2)',padding:'2px 10px',borderRadius:99,fontSize:'0.75rem',fontWeight:600,color:'#5C3010'}}>
                        {m.name} ({m.gender==='M'?'M':'F'}/{m.age})
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div style={{background:'white', padding:'12px 16px'}}>
                <Label required>Relation to {houseData.headPerson.name}</Label>
                <select style={{...inputSt(focused,'relation_to_head'), border:'2px solid rgba(255,107,0,0.35)'}}
                  value={form.relation_to_head}
                  onChange={e=>{ f('relation_to_head',e.target.value); f('house_head_id',String(houseData.headPerson.id)); }}
                  onFocus={fo('relation_to_head')} onBlur={bl}>
                  <option value="">-- Select Relation --</option>
                  {HOUSE_RELATIONS.map(r=><option key={r} value={r}>{r} of {houseData.headPerson.name}</option>)}
                </select>
                {form.relation_to_head && (
                  <div style={{marginTop:8,padding:'7px 12px',background:'#E8F5E9',borderRadius:8,fontSize:'0.8rem',color:'#2E7D32',fontWeight:600}}>
                    ✅ {form.name||'New voter'} is the <strong>{form.relation_to_head}</strong> of <strong>{houseData.headPerson.name}</strong>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* New house */}
          {form.house_number && !houseCheckLoading && houseData && !houseData.exists && (
            <div style={{marginBottom:14,padding:'10px 14px',background:'linear-gradient(135deg,#E8F5E9,#C8E6C9)',borderRadius:10,fontSize:'0.8rem',color:'#2E7D32',fontWeight:600,display:'flex',alignItems:'center',gap:8}}>
              ✨ New house — this voter will be <strong>Head of Family</strong>
            </div>
          )}

          {/* ── SECTION 3: Community ── */}
          <Section icon="📋" title="Community & Profession"/>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:'0 24px'}}>
            <div style={{marginBottom:14}}>
              <Label>Community</Label>
              <select style={inputSt(focused,'community')} value={form.community} onChange={e=>f('community',e.target.value)} onFocus={fo('community')} onBlur={bl}>
                <option value="">-- Select Community --</option>
                {COMMUNITIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{marginBottom:14}}>
              <Label>Profession</Label>
              <select style={inputSt(focused,'profession')} value={form.profession} onChange={e=>f('profession',e.target.value)} onFocus={fo('profession')} onBlur={bl}>
                <option value="">-- Select Profession --</option>
                {PROFESSIONS.map(p=><option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div style={{marginBottom:14}}>
              <Label>Education</Label>
              <select style={inputSt(focused,'degree')} value={form.degree} onChange={e=>f('degree',e.target.value)} onFocus={fo('degree')} onBlur={bl}>
                <option value="">-- Select Education --</option>
                {EDUCATION_LIST.map(e=><option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div style={{marginBottom:14}}>
              <Label>Mother Tongue</Label>
              <select style={inputSt(focused,'mother_tongue')} value={form.mother_tongue} onChange={e=>f('mother_tongue',e.target.value)} onFocus={fo('mother_tongue')} onBlur={bl}>
                <option value="">-- Select --</option>
                {MOTHER_TONGUES.map(m=><option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div style={{marginBottom:14}}>
              <Label>Reference / Volunteer</Label>
              <input style={inputSt(focused,'reference_name')} placeholder="Volunteer who referred" value={form.reference_name} onChange={e=>f('reference_name',e.target.value)} onFocus={fo('reference_name')} onBlur={bl}/>
            </div>
          </div>

          {/* ── GPS ── */}
          <Section icon="📍" title="GPS Location"/>
          {form.latitude && form.longitude && (
            <div style={{marginBottom:12}}>
              <div style={{background:'#E8F5E9',padding:'8px 14px',borderRadius:10,fontSize:'0.8rem',color:'#2E7D32',marginBottom:10,fontWeight:600}}>
                ✅ Lat: {parseFloat(form.latitude).toFixed(6)}, Long: {parseFloat(form.longitude).toFixed(6)}
              </div>
              {/* Google Maps embed — shows location inline */}
              <div style={{borderRadius:14,overflow:'hidden',border:'2px solid rgba(255,107,0,0.2)',boxShadow:'0 4px 16px rgba(0,0,0,0.1)'}}>
                <iframe
                  title="Voter Location"
                  width="100%"
                  height="220"
                  frameBorder="0"
                  style={{display:'block'}}
                  src={`https://maps.google.com/maps?q=${form.latitude},${form.longitude}&z=16&output=embed`}
                  allowFullScreen
                />
              </div>
              <div style={{fontSize:'0.7rem',color:'#9E6040',marginTop:6,textAlign:'center'}}>
                📍 Location preview — tap to verify position
              </div>
            </div>
          )}
          <button type="button" onClick={getLocation} disabled={gettingLoc} style={{width:'100%',padding:'11px',border:'2px solid rgba(255,107,0,0.3)',borderRadius:12,background:'transparent',color:'#E55100',fontWeight:700,fontSize:'0.88rem',cursor:gettingLoc?'not-allowed':'pointer',fontFamily:"'Outfit',sans-serif",transition:'all 0.2s',display:'flex',alignItems:'center',justifyContent:'center',gap:8,marginBottom:20}}>
            {gettingLoc?<><span style={{width:14,height:14,border:'2px solid rgba(255,107,0,0.3)',borderTopColor:'#FF6B00',borderRadius:'50%',display:'inline-block',animation:'spin 0.7s linear infinite'}}/>Getting GPS...</>:form.latitude?'📍 Re-capture GPS Location':'📍 Capture GPS Location'}
          </button>

          {/* Submit */}
          <button onClick={handleSubmit} disabled={loading} style={{width:'100%',padding:'15px',border:'none',borderRadius:16,background:loading?'#ccc':'linear-gradient(135deg,#E55100,#FF6B00,#FF9933)',backgroundSize:'200% 200%',animation:loading?'none':'gradientShift 3s ease infinite',color:'white',fontWeight:700,fontSize:'1rem',cursor:loading?'not-allowed':'pointer',fontFamily:"'Outfit',sans-serif",boxShadow:loading?'none':'0 6px 24px rgba(255,107,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',gap:10}}>
            {loading?<><span style={{width:16,height:16,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'white',borderRadius:'50%',display:'inline-block',animation:'spin 0.7s linear infinite'}}/>Enrolling...</>:'✅ Enroll Voter'}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes gradientShift{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}} @keyframes fadeInUp{from{transform:translateY(12px);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
    </div>
  );
}